import { useDisclosure } from '@mantine/hooks';
import {
  Box, Button, Flex, Text
} from '@huspy/briks-web';
import { useTranslation } from 'react-i18next';
import { useForm, zodResolver } from '@mantine/form';
import { BankMultiSelect } from '@modules/banks/features/BankMultiSelect';
import { BaseSlido } from '@modules/core/components/BaseSlido';
import {
  Bank,
  User,
  Users,
  UserPlus,
  UserSquare,
  Plus,
} from '@huspy/briks-icons';
import {
  APPLICANT_TYPE,
  MORTGAGE_USAGE,
  OPPORTUNITY_CURRENCY,
} from '@modules/opportunities/entities/opportunity/const';
import {
  CaseFormInput,
  CaseFormSelectInput,
  SectionHeader,
} from '@modules/opportunities/presentation/pages/CaseCreation/components/CaseInputs';
import { useLocation, useNavigate } from '@tanstack/react-router';
import { scrollToError } from '@shared/utils';
import useCreateOpportunity from '@modules/opportunities/entities/opportunity/mutation/useCreateOpportunity';
import { CreateOpportunityApplicantDTO } from '@modules/opportunities/entities/opportunity/api/dto/createOpportunity.dto';
import { useOpportunityFormErrorHandler } from '@modules/opportunities/presentation/hooks/useHandleErrors';
import {
  getApplicationType,
  getEmploymentType,
  getMaxBankCount,
  getMortgageType,
  getResidencyType,
  getStates,
  getTransactionType,
  NATIONALITIES,
} from '@modules/opportunities/presentation/pages/const';
import React, { useEffect, useState } from 'react';
import { CaseCreationBasicInfoRoute } from '@modules/opportunities/presentation/routes';
import { Tabs } from '@mantine/core';
import briksTheme from '@shared/briks-theme';
import {
  trackAmplitudeEvent,
  clearAmplitudeEventContext,
} from '@shared/analytics/amplitude';
import { USER_EVENTS } from '@shared/analytics/events';
import { ExistingClientSelect } from '@modules/clients/presentation/v2/features/ExistingClientSelect';
import {
  IS_SPAIN_ENV,
  IS_UAE_ENV,
  slidoDeepLink
} from '@modules/core/utils';
import { MortgageType, MortgageUsage } from '@modules/opportunities/entities/opportunity/opportunity.entity';
import useFeatureFlags from '@modules/core/hooks/useFeatureFlags';
import { caseBasicInfoSchema } from './validator';
import { MortgageUsageSelection } from './MortgageUsageSelection';

type ExistingClient = React.ComponentProps<typeof ExistingClientSelect>['defaultValue'];

type SelectedBanks = (NonNullable<
React.ComponentProps<typeof BankMultiSelect>['defaultValue']
>[number] & {
  mortgageType: MortgageType;
})[];

type CaseSlidoProps = {
  hideTabs?: boolean;
  tab?: 'newClient' | 'existingClient';
  existingClientData?: ExistingClient;
  actionButton?: React.ReactNode;
};

export const CaseSlido = ({
  hideTabs = false,
  tab = 'newClient',
  existingClientData,
  actionButton,
}: CaseSlidoProps) => {
  const { hash } = useLocation();
  const [opened, { open, close }] = useDisclosure(hash === slidoDeepLink.CASE_CREATION);
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState<string | null>(tab);
  const {
    mutateAsync: createOpportunity,
    error: createOpportunityErrors,
    isPending: isCreateOpportunityLoading,
  } = useCreateOpportunity();
  const { isFeatureActive } = useFeatureFlags();
  const isMortgageUsageActive = isFeatureActive('opportunities.mortgageUsage');
  const [showForm, setShowForm] = useState(false);

  const { t } = useTranslation();
  const form = useForm({
    initialValues: {
      client_external_id: existingClientData?.id ?? '',
      first_name: existingClientData?.firstName ?? '',
      last_name: existingClientData?.lastName ?? '',
      email: existingClientData?.email ?? '',
      mobile: existingClientData?.mobile ?? '',
      citizen_status: '',
      employment_status: '',
      dob: '',
      nationality: '',
      selected_banks: [] as SelectedBanks,
      type_of_transaction: '',
      mortgage_type: '',
      application_type: '',
      state: '',
      amount: undefined,
      applicant_first_name: '',
      applicant_last_name: '',
      mortgage_usage: MORTGAGE_USAGE.RESIDENTIAL as MortgageUsage,
      is_assisted: false,
    },
    validateInputOnChange: true,
    validate: zodResolver(caseBasicInfoSchema),
  });

  const isNotMainApplicant = form.values.application_type
    && form.values.application_type !== APPLICANT_TYPE.mainApplicant;

  const onSubmit = async () => {
    clearAmplitudeEventContext();

    const data = form.values;

    // Track attempt
    trackAmplitudeEvent(USER_EVENTS.CASE.CASE_CREATION_ATTEMPTED, {
      addToContext: true,
      client_id: data.client_external_id,
      banks: data.selected_banks.map((sb) => sb.id),
    });

    form.validate();
    if (!form.isValid()) {
      // Track fail
      trackAmplitudeEvent(USER_EVENTS.CASE.CASE_CREATION_FAILED, {
        useContext: true,
        error_message: form.errors,
      });

      scrollToError(form.errors);
      return;
    }

    const isExistingClient = activeTab === 'existingClient' && data.client_external_id;

    const applicants = [
      {
        first_name: data.first_name,
        last_name: data.last_name,
        email: data.email,
        mobile: data.mobile,
        applicant_type: APPLICANT_TYPE.mainApplicant,
        employment_status: data.employment_status,
        citizen_status: data.citizen_status,
        dob: data.dob,
        nationality: data.nationality,
      }
    ] as CreateOpportunityApplicantDTO[];

    if (data.applicant_first_name && data.applicant_last_name) {
      applicants.push({
        first_name: data.applicant_first_name,
        last_name: data.applicant_last_name,
        applicant_type: data.application_type,
      });
    }
    const opportunity = await createOpportunity({
      applicants,
      state: data.state,
      amount: data.amount ?? 0,
      type_of_transaction: data.type_of_transaction,
      banks: data.selected_banks.map((sb) => ({
        external_id: sb.id,
        mortgage_type: sb.mortgageType,
      })),
      mortgage_usage: data.mortgage_usage,
      is_assisted: data.is_assisted,
      ...(isExistingClient && { client_external_id: data.client_external_id }),
    });

    navigate({
      to: CaseCreationBasicInfoRoute.to,
      params: { opportunityId: opportunity.opportunity_external_id },
    });
  };

  useOpportunityFormErrorHandler({
    opportunityErrors: createOpportunityErrors,
    form,
  });

  useEffect(() => {
    if (!isNotMainApplicant) {
      form.setFieldValue('applicant_first_name', '');
      form.setFieldValue('applicant_last_name', '');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isNotMainApplicant]);

  form.watch('mortgage_type', ({ value }) => {
    const updatedBanksSelection = form.values.selected_banks.map((bank) => ({
      ...bank,
      mortgageType: value as MortgageType,
    }));
    form.setFieldValue('selected_banks', updatedBanksSelection);
  });

  useEffect(() => {
    if (!hideTabs) {
      form.setValues({
        ...form.values,
        first_name: '',
        last_name: '',
        mobile: '',
        email: '',
        client_external_id: '',
      });
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab]);

  const renderMortgageUsageSelection = () => (
    <BaseSlido
      opened={ opened }
      close={ close }
      title={ (
        <Flex direction='column' gap='2' p='6' pb='0'>
          <Text size='5xl'>
            {t('clients.formSections.title')}
            {' '}
          </Text>
          <Text size='md' color='neutral.500'>
            {t('clients.formSections.mortgageUsageSelectionSlido.description')}
          </Text>
        </Flex>
      ) }
      hasDivider
      footer={ (
        <Flex justify='end' gap='2'>
          <Button
            type='button'
            onClick={ close }
            variant='secondary'
            data-test='discard-btn'
          >
            {t('clients.formSections.discardButton')}
          </Button>
          <Button data-test='continue-form-btn' onClick={ () => setShowForm(true) }>
            {t('clients.formSections.mortgageUsageSelectionSlido.continueBtn')}
          </Button>
        </Flex>
      ) }
    >
      <MortgageUsageSelection form={ form } />
    </BaseSlido>
  );

  const renderForm = () => (
    <BaseSlido
      opened={ opened }
      close={ () => {
        close();
        setShowForm(false);
      } }
      title={ (
        <Flex direction='column' gap='2' p='6' pb='0'>
          <Text size='5xl'>
            {t('clients.formSections.title')}
            {' '}
          </Text>
          <Text size='md' color='neutral.500'>
            {t('clients.formSections.description')}
          </Text>
        </Flex>
      ) }
      hasDivider={ hideTabs }
      footer={ (
        <Flex justify='end' gap='2'>
          <Button
            type='button'
            onClick={ () => {
              close();
              setShowForm(false);
            } }
            variant='secondary'
            data-test='discard-btn'
          >
            {t('clients.formSections.discardButton')}
          </Button>
          <Button
            type='submit'
            disabled={ isCreateOpportunityLoading }
            loading={ isCreateOpportunityLoading }
            variant={ isCreateOpportunityLoading ? 'secondary' : 'primary' }
            data-test='save-btn'
            onClick={ onSubmit }
          >
            {t('clients.formSections.saveButton')}
          </Button>
        </Flex>
      ) }
    >
      <Tabs defaultValue={ tab } onChange={ setActiveTab }>
        <Tabs.List display={ hideTabs ? 'none' : 'display' }>
          <Tabs.Tab
            value='newClient'
            leftSection={ (
              <Flex align='center' justify='center' h='100%' w='100%' mr='1'>
                <UserPlus color={ briksTheme.colors['neutral.600'] } />
              </Flex>
            ) }
            py='8px'
            pl='24px'
          >
            <Text size='md' mr='4' color='neutral.600' whiteSpace='nowrap'>
              {t('clients.formSections.newClientTab')}
            </Text>
          </Tabs.Tab>
          <Tabs.Tab
            value='existingClient'
            leftSection={ (
              <Flex align='center' justify='center' h='100%' w='100%' mr='1'>
                <UserSquare color={ briksTheme.colors['neutral.600'] } />
              </Flex>
            ) }
          >
            <Text size='md' mr='4' color='neutral.600' whiteSpace='nowrap' data-test='existing-client-tab'>
              {t('clients.formSections.existingClientTab')}
            </Text>
          </Tabs.Tab>
        </Tabs.List>
        <form onSubmit={ form.onSubmit(onSubmit, scrollToError) }>
          <Flex direction='column' gap='4' p='6'>
            <SectionHeader
              title={ t('clients.formSections.mainApplicantInfo') }
              Icon={ User }
            />
            <Tabs.Panel value='newClient'>
              <Flex gap='4'>
                <CaseFormInput
                  name='first_name'
                  label='firstName'
                  data-test='input-firstname'
                  form={ form }
                />
                <CaseFormInput
                  name='last_name'
                  label='lastName'
                  data-test='input-lastname'
                  form={ form }
                />
              </Flex>
              <Flex gap='4'>
                <CaseFormInput
                  name='email'
                  label='email'
                  data-test='input-email'
                  form={ form }
                />
                <CaseFormInput
                  name='mobile'
                  label='phoneNumber'
                  data-test='input-mobile'
                  form={ form }
                  type='phone'
                />
              </Flex>
            </Tabs.Panel>
            <Tabs.Panel value='existingClient'>
              <ExistingClientSelect
                label={ t('clients.formSections.selectClient') }
                placeholder={ t('clients.formSections.search&select') }
                onValueChange={ (clientData: ExistingClient) => {
                  form.setValues({
                    ...form.values,
                    first_name: clientData!.firstName,
                    last_name: clientData!.lastName,
                    mobile: clientData!.mobile,
                    email: clientData!.email,
                    client_external_id: clientData!.id,
                  });
                } }
                defaultValue={ existingClientData }
                disabled={ !!existingClientData }
                isError={ !!form.errors?.first_name }
              />
            </Tabs.Panel>
            <Flex gap='4'>
              <CaseFormSelectInput
                name='citizen_status'
                data-test='select-residency-status'
                label='residencyStatus'
                form={ form }
                data={ getResidencyType() }
              />
              <CaseFormSelectInput
                name='employment_status'
                data-test='select-employment-status'
                label='employmentStatus'
                form={ form }
                data={ getEmploymentType() }
              />
            </Flex>
            <Flex gap='4'>
              <CaseFormInput
                name='dob'
                label='dob'
                data-test='select-main-dob'
                form={ form }
                type='date'
              />
              <CaseFormSelectInput
                name='nationality'
                data-test='select-nationality'
                label='nationality'
                data={ NATIONALITIES }
                form={ form }
              />
            </Flex>
            <SectionHeader
              title={ t('clients.formSections.mortgageInfo') }
              Icon={ Bank }
            />
            <Box id='selected_banks' data-test='select-banks'>
              <BankMultiSelect
                label={ t('clients.formLabels.banks') }
                maxBanks={ getMaxBankCount() }
                placeholder={ t('clients.formLabels.banks') }
                onValueChange={ (selected_banks) => form.setFieldValue('selected_banks', selected_banks as SelectedBanks) }
                isError={ !!form.getInputProps('selected_banks')?.error }
                error={ form.getInputProps('selected_banks')?.error }
              />
            </Box>
            <Flex gap='4'>
              {IS_SPAIN_ENV && (
                <CaseFormSelectInput
                  name='mortgage_type'
                  data-test='select-mortgage-type'
                  label='mortgageType'
                  form={ form }
                  data={ getMortgageType() }
                />
              )}
              <CaseFormSelectInput
                name='application_type'
                data-test='select-application-type'
                label='applicationType'
                form={ form }
                data={ getApplicationType() }
              />
            </Flex>
            <Flex gap='4'>
              <CaseFormSelectInput
                name='state'
                data-test='select-state'
                label='province'
                form={ form }
                data={ getStates() }
              />
              <CaseFormInput
                name='amount'
                data-test='loan-amount'
                label='loanAmount'
                form={ form }
                type='number'
                trail={ IS_UAE_ENV
                  ? OPPORTUNITY_CURRENCY.AED
                  : OPPORTUNITY_CURRENCY.EUR }
              />
            </Flex>
            <CaseFormSelectInput
              name='type_of_transaction'
              data-test='select-transaction-type'
              label='transactionType'
              form={ form }
              data={ getTransactionType() }
            />
            {isNotMainApplicant && (
              <>
                <SectionHeader
                  title={ t('clients.formSections.applicantInfo') }
                  Icon={ Users }
                />
                <Flex gap='4'>
                  <CaseFormInput
                    data-test='input-applicant-firstname'
                    name='applicant_first_name'
                    label='firstName'
                    form={ form }
                  />
                  <CaseFormInput
                    data-test='input-applicant-lastname'
                    name='applicant_last_name'
                    label='lastName'
                    form={ form }
                  />
                </Flex>
              </>
            )}
          </Flex>
        </form>
      </Tabs>
    </BaseSlido>
  );

  return (
    <>
      {isMortgageUsageActive && !showForm ? renderMortgageUsageSelection() : renderForm()}
      {actionButton ? (
        <Box onClick={ open }>{actionButton}</Box>
      ) : (
        <Button onClick={ open } data-test='create-case-btn'>
          <Plus width={ 24 } color='inherit' />
          {t('opportunity.caseSubmission.title')}
        </Button>
      )}
    </>
  );
};
